import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import { useStore } from '../../store';
import { Label, Paragraph } from '../../ui/typography';
import { addProduct } from '../../store/store.actions';
import { getFormattedAmount } from '../../utils/accounting';
import { parseMetafields } from '../../utils/product';
import SpamFlaggingContext from '../../context/spamFlagging';
import { CartEventLocation } from '../../types/common';
import { analyticsTrackAddToCart } from '../../utils/analytics';

const SingleAddOnWrapper = styled.div<{ isVisible: boolean }>`
  display: flex;
  column-gap: 6px;
  position: relative;
  align-items: center;
  cursor: pointer;
  margin: 0 0 16px 0;
  padding: 8px 12px;
  border: ${({ theme }) => `2px solid ${theme.colors.primary}`};
  border-radius: 8px;
  padding-right: 8px;
  background-color: ${({ theme }) => theme.colors.neutral1};

  transition:
    opacity 0.25s,
    background-color 0.25s;
  opacity: ${({ isVisible }) => (isVisible ? '100%' : '0%')};

  &:last-child {
    margin-bottom: 0;
  }
  &:hover {
    background-color: #f3f2fe;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  height: 100px;
  width: 100px;
  border-radius: 0 8px 8px 8px;
  overflow: hidden;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    height: 80px;
    width: 80px;
    flex-shrink: 0.5;
  }
`;

const PlusImageWrapper = styled.div`
  min-width: 28px;
  min-height: 28px;
  display: flex;
  margin-left: auto;
  align-items: center;
`;

const AddOnTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const AddOnContentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const SaleText = styled.span`
  color: ${({ theme }) => theme.colors.error};
  font-weight: 700;
`;

const SalePriceWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  width: fit-content;
`;

type SingleAddOnProps = {
  product: Shopify.Product;
  setAddOnSuggestions: React.Dispatch<React.SetStateAction<Shopify.Product | undefined>>;
  setIsContainerVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAddOnAccepted: React.Dispatch<React.SetStateAction<boolean>>;
  pinAddOnProduct: Shopify.Product | null;
  isPlushPinBundle: boolean;
};

const SingleAddOn: React.FC<SingleAddOnProps> = ({
  product,
  setAddOnSuggestions,
  setIsContainerVisible,
  setIsAddOnAccepted,
  pinAddOnProduct,
  isPlushPinBundle,
}: SingleAddOnProps) => {
  const { state, dispatch } = useStore();
  const { incrementSpamCounter, isSpam } = useContext(SpamFlaggingContext);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const metafields = parseMetafields(product.metafields);

  const handleAddOnClicked = (product: Shopify.Product) => {
    setIsAddOnAccepted(true);
    setIsContainerVisible(false);
    setAddOnSuggestions(undefined);

    addProduct(state.cart, 1, product.variants.edges[0]?.node).then((action) => dispatch(action));

    const metafields = parseMetafields(product.metafields);
    incrementSpamCounter();
    if (!isSpam) {
      analyticsTrackAddToCart(
        product,
        1,
        state.user,
        CartEventLocation.CartAddOn,
        product.variants.edges[0]?.node,
        metafields.campaignID,
        !!isPlushPinBundle,
      );
    }
  };

  setTimeout(() => {
    setIsVisible(true);
  }, 0);

  return (
    <SingleAddOnWrapper onClick={() => handleAddOnClicked(product)} isVisible={isVisible}>
      <ImageWrapper>
        <Image
          src={product.images.edges[0]?.node.transformedSrc}
          alt={product.images.edges[0]?.node.altText || 'variant image'}
          layout="fill"
          objectFit="contain"
          quality={90}
          unoptimized
        />
      </ImageWrapper>
      <AddOnTextWrapper>
        {isPlushPinBundle &&
          (pinAddOnProduct?.id === product.id ? (
            <AddOnContentRow>
              <Paragraph>Bundle & Save 🎉</Paragraph>
            </AddOnContentRow>
          ) : metafields.pinAddOn ? (
            <AddOnContentRow>
              <Paragraph>
                Get <SaleText>$6 off</SaleText> your pin when you complete your collection!
              </Paragraph>
            </AddOnContentRow>
          ) : (
            <></>
          ))}

        <AddOnContentRow>
          <Label>{product.title}</Label>
        </AddOnContentRow>
        <AddOnContentRow>
          {isPlushPinBundle && pinAddOnProduct?.id === product.id ? (
            <SalePriceWrapper>
              <div className="mr-2">
                <Label color="neutral-300" className="line-through">
                  ${getFormattedAmount(product.variants.edges[0].node.price.amount)}
                </Label>
              </div>
              <Label className="text-error uppercase">$9.99</Label>
            </SalePriceWrapper>
          ) : (
            <Paragraph>${getFormattedAmount(product.variants.edges[0].node.price.amount)}</Paragraph>
          )}
        </AddOnContentRow>
      </AddOnTextWrapper>
      <PlusImageWrapper>
        <Image src="/assets/icons/addOn.png" alt="add to cart" width={28} height={28} />
      </PlusImageWrapper>
    </SingleAddOnWrapper>
  );
};

export default SingleAddOn;
